import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from '../components/ui/Card';
import TransactionDetailModal from '../components/transaction-detail-modal';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Button } from '../components/ui/Button';
import { Receipt, Search, X } from 'lucide-react';
import { BillingTransaction } from '../types/admin';
import { format } from 'date-fns';

const Transactions: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const accountIdFilter = searchParams.get('account_id') || '';
  const analysisIdFilter = searchParams.get('analysis_id') || '';
  const [localAccountId, setLocalAccountId] = useState(accountIdFilter);
  const [localAnalysisId, setLocalAnalysisId] = useState(analysisIdFilter);
  const [selectedTransaction, setSelectedTransaction] = useState<BillingTransaction | null>(null);

  const params = useMemo(() => ({
    account_id: accountIdFilter || undefined,
    analysis_id: analysisIdFilter || undefined,
    page,
    page_size: pageSize,
  }), [accountIdFilter, analysisIdFilter, page]);

  const { data: transactions, isLoading, error } = useQuery({
    queryKey: ['transactions', params],
    queryFn: () => adminApiClient.getTransactions(params),
  });

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    if (localAccountId.trim()) {
      newParams.set('account_id', localAccountId.trim());
    }
    if (localAnalysisId.trim()) {
      newParams.set('analysis_id', localAnalysisId.trim());
    }
    setSearchParams(newParams);
    setPage(1);
  };

  const clearFilters = () => {
    setLocalAccountId('');
    setLocalAnalysisId('');
    setSearchParams({});
    setPage(1);
  };

  const formatCents = (cents: number) => {
    const sign = cents < 0 ? '-' : '+';
    return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      payment: 'Payment',
      auto_reload: 'Auto Reload',
      usage_deduction: 'Usage',
      refund: 'Refund',
      adjustment: 'Adjustment',
      admin_credit: 'Admin Credit',
    };
    return labels[type] || type;
  };

  const getTransactionVariant = (type: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const variants: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
      payment: 'success',
      auto_reload: 'success',
      usage_deduction: 'warning',
      refund: 'info',
      adjustment: 'default',
      admin_credit: 'success',
    };
    return variants[type] || 'default';
  };

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
    };
    return variants[status] || 'default';
  };

  const hasFilters = accountIdFilter || analysisIdFilter;
  const totalPages = transactions?.total_pages ?? 0;

  if (isLoading) {
    return <Loading message="Loading transactions..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Billing Transactions</h1>
        <p className="text-gray-400 mt-1">
          View all billing transactions across the platform
        </p>
      </div>

      {error && (
        <Card>
          <div className="p-6 text-red-400">
            Failed to load transactions: {(error as Error).message}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="p-6">
          <form onSubmit={handleFilter} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Account ID
              </label>
              <input
                type="text"
                value={localAccountId}
                onChange={(e) => setLocalAccountId(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Filter by account..."
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Analysis ID
              </label>
              <input
                type="text"
                value={localAnalysisId}
                onChange={(e) => setLocalAnalysisId(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Filter by analysis..."
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" variant="primary">
                <Search className="w-4 h-4 mr-2" />
                Filter
              </Button>
              {hasFilters && (
                <Button type="button" variant="ghost" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </form>
        </div>
      </Card>

      {/* Summary */}
      {transactions && transactions.data && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>
            Showing {transactions.data.length} of {transactions.total} transactions
          </span>
          {hasFilters && (
            <span className="flex items-center">
              <Receipt className="w-4 h-4 mr-1" />
              Filtered results
            </span>
          )}
        </div>
      )}

      {/* Transactions Table */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>All Transactions</CardTitle>
        </div>

        {transactions && transactions.data && transactions.data.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Date</TableCell>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Account</TableCell>
                  <TableCell header>Amount</TableCell>
                  <TableCell header>Balance After</TableCell>
                  <TableCell header>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.data.map((tx) => (
                  <TableRow
                    key={tx.id}
                    className="cursor-pointer hover:bg-gray-750"
                    onClick={() => setSelectedTransaction(tx)}
                  >
                    <TableCell>
                      <span className="text-gray-300">
                        {format(new Date(tx.created_at), 'MMM d, yyyy HH:mm')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getTransactionVariant(tx.type)}>
                        {getTransactionTypeLabel(tx.type)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(tx.status)}>
                        {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {tx.account_name ? (
                        <Link
                          to={`/admin/accounts/${tx.account_id}`}
                          className="text-sm text-primary-400 hover:text-primary-300 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {tx.account_name}
                        </Link>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={tx.amount_cents < 0 ? 'text-red-400 font-medium' : 'text-green-400 font-medium'}>
                        {formatCents(tx.amount_cents)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">${(tx.balance_after_cents / 100).toFixed(2)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400 text-sm max-w-xs truncate block">
                        {tx.description}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Page {page} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No transactions found
          </div>
        )}
      </Card>

      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          formatCents={formatCents}
          getTransactionTypeLabel={getTransactionTypeLabel}
          getTransactionVariant={getTransactionVariant}
          getStatusVariant={getStatusVariant}
        />
      )}
    </div>
  );
};

export default Transactions;
